import { useState, useMemo, useCallback, useEffect } from "react";
import { HiPhoneIncoming } from "react-icons/hi";
import { FaPlus } from "react-icons/fa6";
import {
  Button,
  DateInput,
  InputField,
  DropDownInput,
  DataTable,
} from "@/components";
import NewEscalationForm from "./NewEscalationForm";
import RequestCallForm from "./RequestCallForm";
import ColumnHelper from "@/utils/ColumnHelper";
import { useFilteredData } from "@/";
import { getEscalationTypeLabel } from "../../utils/Data";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";
import { Link } from "react-router-dom";
import { SpaceWrapper } from "../../components";
import { useDispatch, useSelector } from "react-redux";
import { fetchEscalationData, selectAllEscalations } from "../../app/slices";
import { subDays } from "date-fns";
const Support = () => {
  const dispatch = useDispatch();
  const dateRange =
    useSelector((state) => state.dashboard?.data?.dateRange) || {};
  const currentDate = new Date();
  const startOfMonthDate = dateRange?.startDate
    ? new Date(dateRange.startDate)
    : subDays(currentDate, 30); // Last 30 days
  const endOfMonthDate = dateRange?.endDate
    ? new Date(dateRange.endDate)
    : currentDate;
  const [filters, setFilters] = useState({
    date: { from: startOfMonthDate, to: endOfMonthDate },
    escalationId: "",
    shippingId: "",
    escalationType: "",
    status: "",
  });
  const fetchData = async () => {
    const response = await dispatch(
      fetchEscalationData({
        startDate: filters.date.from,
        endDate: filters.date.to,
      }),
    );
    console.log("response::::::", response);
  };

  useEffect(() => {
    fetchData();
  }, [filters.date, dispatch]);

  const [sheetOpen, setSheetOpen] = useState(false);
  const [sheetCallOpen, setSheetCallOpen] = useState(false);
  const [appliedFilters, setAppliedFilters] = useState(filters);
  const [resetPageCounter, setResetPageCounter] = useState(0);
  const escalationType = useMemo(
    () => [
      {
        label: "Pickup",
        value: "pickup",
      },
      {
        label: "Pre Delivery",
        value: "preDelivery",
      },
      {
        label: "Post Delivery",
        value: "postDelivery",
      },
      {
        label: "RTO",
        value: "rto",
      },
      {
        label: "Claim",
        value: "claims",
      },
      {
        label: "Finance",
        value: "finance",
      },
      {
        label: "KYC",
        value: "kyc",
      },
      {
        label: "Weight Dispute",
        value: "weightDispute",
      },
      {
        label: "Technical Support",
        value: "technicalSupport",
      },
      { label: "Other", value: "other" },
    ],
    [],
  );
  const statusType = useMemo(
    () => [
      { label: "Open", value: "Open" },
      { label: "In Progress", value: "In Progress" },
      { label: "Pending", value: "Pending" },
      { label: "Closed", value: "Closed" },
      { label: "Resolved", value: "Resolved" },
      { label: "Escalated", value: "Escalated" },
    ],
    [],
  );
  const escalations = useSelector(selectAllEscalations) || [];
  console.log("escalations>>>", escalations);
  // Filter configuration
  const filterConfig = useMemo(
    () => ({
      shippingId: {
        type: "comma",
        itemKey: "shipments.shippingId",
      },
      escalationId: { type: "partial", itemKey: "escalationId" },
      status: { type: "partial", itemKey: "status" },
      escalationType: { type: "exact", itemKey: "escalationType" },
      date: { type: "date", itemKey: "escalationDate" },
    }),
    [],
  );
  // Format date values
  const formatDate = useCallback((dateString) => {
    return new Date(dateString).toLocaleDateString("en-IN", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  }, []);
  // Memoize filtered data to prevent unnecessary re-renders
  const { data: finalFilteredData, isLoading } = useFilteredData(
    escalations,
    appliedFilters,
    filterConfig,
  );

  const handleView = (escalationId) => {
    console.log(escalationId);
  };

  // Memoized table columns
  const columns = useMemo(
    () => [
      ColumnHelper({
        header: "Escalation ID",
        accessorKey: "escalationId",
        meta: {
          tooltip: (row) => row.escalationId || "N/A",
        },
      }),
      ColumnHelper({
        header: "Escalation Date",
        accessorKey: "escalationDate",
        cell: (info) => formatDate(info.row.original.escalationDate),
        meta: {
          tooltip: (row) => formatDate(row.escalationDate),
        },
      }),
      ColumnHelper({
        header: "Status",
        accessorKey: "status",
        meta: {
          tooltip: (row) => row.status || "N/A",
        },
      }),
      ColumnHelper({
        header: "Shipping Id",
        accessorKey: "",
        cell: (info) =>
          info.row.original.shipments?.map((s) => s?.shippingId).join(",") ||
          "N/A",
        meta: {
          tooltip: (row) =>
            row.shipments?.map((s) => s?.shippingId).join(",") || "N/A",
        },
      }),
      ColumnHelper({
        header: "Escalation Type",
        accessorKey: "escalationType",
        cell: (info) =>
          getEscalationTypeLabel(info.row.original.escalationType) || "N/A",
        meta: {
          tooltip: (row) => row.escalationType || "N/A",
        },
      }),
      ColumnHelper({
        header: "Actions",
        accessorKey: "_id",
        cell: (info) => (
          <div className="flex gap-1 justify-center items-center">
            <Link to={`/support/details/${info.row.original._id}`}>
              <Button
                size="xs"
                onClick={() => handleView(info.row.original._id)}
              >
                View
              </Button>
            </Link>
          </div>
        ),
        meta: {
          tooltip: () => "View escalation details",
        },
      }),
    ],
    [],
  );

  // Handle filter changes
  const handleChange = useCallback((key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
  }, []);

  const handleDateChange = useCallback((dateRange) => {
    setFilters((prev) => ({
      ...prev,
      date: { from: dateRange?.from || "", to: dateRange?.to || "" },
    }));
  }, []);

  // Apply filters without resetting raw data
  const handleApply = useCallback(() => {
    setAppliedFilters(filters);
    setResetPageCounter((prev) => prev + 1);
  }, [filters]);

  // Reset filters properly
  const handleReset = useCallback(() => {
    const initialFilters = {
      date: { from: startOfMonthDate, to: endOfMonthDate },
      escalationId: "",
      shippingId: "",
      escalationType: "",
      status: "",
    };
    setFilters(initialFilters);
    setAppliedFilters(initialFilters);
    setResetPageCounter((prev) => prev + 1);
  }, []);

  return (
    <SpaceWrapper>
      <div className="w-full flex-1 flex flex-col gap-4 p-4 pb-20 md:pb-4 relative max-w-[2000px]">
        {/* Header */}
        <div className="flex flex-col md:flex-row gap-2 justify-between">
          <h2 className="text-headline2 text-secondary/95 font-nunito font-bold">
            Escalations
          </h2>
          <div className="flex  gap-2">
            {/* Request Callback */}
            <Sheet open={sheetCallOpen} onOpenChange={setSheetCallOpen}>
              <SheetTrigger asChild>
                <Button variant="default" size="md" icon={HiPhoneIncoming}>
                  Request Callback
                </Button>
              </SheetTrigger>
              <SheetContent aria-describedby="">
                <SheetHeader>
                  <SheetTitle>Request Callback</SheetTitle>
                  <RequestCallForm setSheetOpen={setSheetCallOpen} />
                </SheetHeader>
              </SheetContent>
            </Sheet>

            {/* New Escalation */}
            <Sheet open={sheetOpen} onOpenChange={setSheetOpen}>
              <SheetTrigger asChild>
                <Button variant="default" size="md" icon={FaPlus}>
                  New Escalation
                </Button>
              </SheetTrigger>
              <SheetContent aria-describedby="">
                <SheetHeader>
                  <SheetTitle>New Escalation</SheetTitle>
                  <NewEscalationForm setSheetOpen={setSheetOpen} />
                </SheetHeader>
              </SheetContent>
            </Sheet>
          </div>
        </div>

        {/* Filters */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 items-end">
          {/* Date Filter */}
          <DateInput
            label="Date Filter"
            placeholder="Select From & To date"
            mode="range"
            value={filters.date}
            onChange={handleDateChange}
          />

          {/* Escalation ID Input */}
          <InputField
            label="Escalation ID"
            placeholder="Enter Escalation ID"
            name="escalationId"
            value={filters.escalationId}
            onChange={(value) => handleChange("escalationId", value)}
          />

          {/* Shipping ID Input */}
          <InputField
            label="Shipping ID"
            placeholder="Enter Shipping ID"
            name="shippingId"
            value={filters.shippingId}
            onChange={(value) => handleChange("shippingId", value)}
          />

          {/* Escalation Type Dropdown */}
          <DropDownInput
            label="Escalation Type"
            name="escalationType"
            placeholder="Select Escalation Type"
            options={escalationType}
            value={filters.escalationType}
            onChange={(value) => handleChange("escalationType", value)}
          />

          {/* Status Dropdown */}
          <DropDownInput
            label="Status"
            name="status"
            placeholder="Select Status"
            options={statusType}
            value={filters.status}
            onChange={(value) => handleChange("status", value)}
          />

          {/* Action Buttons */}
          <div className="flex gap-2 w-full md:w-fit justify-end items-end">
            <Button
              variant="default"
              size="md"
              onClick={handleApply}
              className="h-12"
            >
              Apply
            </Button>
            <Button
              variant="outline"
              size="md"
              onClick={handleReset}
              className="h-12"
            >
              Reset
            </Button>
          </div>
        </div>
        <DataTable
          columns={columns}
          tittle="Escalations"
          data={finalFilteredData}
          showCheckbox={false}
          isLoading={isLoading}
          resetPage={resetPageCounter}
        />
      </div>
    </SpaceWrapper>
  );
};

export default Support;
